/**
 * Track marks and route coverage: where plan items sit along each track.
 * Marks are derived, never stored.
 */
import { lineMidpoint, nearestOnTrack, positionAt, segmentDistances, } from "./measure.js";
import { elevationAt } from "./profile.js";
import { pointInPolygon, viewAreaToLngLats, } from "./view-areas.js";
export const DEFAULT_MARK_SNAP_DISTANCE_M = 100;
export const DEFAULT_MARK_MERGE_DISTANCE_M = 200;
/** Points of an item that are mapped onto tracks. */
export function itemAnchors(item) {
    if (item.point)
        return [item.point];
    const line = item.line ?? [];
    if (line.length === 0)
        return [];
    if (line.length === 1)
        return [line[0]];
    return [lineMidpoint(line), line[0], line[line.length - 1]];
}
/**
 * Passes of a point along a track: every run of consecutive segments within
 * the snap distance is one pass, and its closest point is the mark.
 */
export function passesNear(track, point, snapDistanceM) {
    const distances = segmentDistances(track, point);
    const passes = [];
    let current = null;
    for (const { m, d } of distances) {
        if (d <= snapDistanceM) {
            if (!current || d < current.offsetM)
                current = { m, offsetM: d };
        }
        else if (current) {
            passes.push(current);
            current = null;
        }
    }
    if (current)
        passes.push(current);
    return passes;
}
/** Merge passes closer than mergeDistanceM along the track; keep the closest. */
function mergePasses(passes, mergeDistanceM) {
    const sorted = [...passes].sort((a, b) => a.m - b.m);
    const groups = [];
    for (const pass of sorted) {
        const group = groups[groups.length - 1];
        if (group && pass.m - group[group.length - 1].m < mergeDistanceM) {
            group.push(pass);
        }
        else {
            groups.push([pass]);
        }
    }
    return groups.map((group) => group.reduce((best, p) => (p.offsetM < best.offsetM ? p : best)));
}
/** Marks of one item on one track, one per pass. */
export function trackMarks(anchors, track, { snapDistanceM = DEFAULT_MARK_SNAP_DISTANCE_M, mergeDistanceM = DEFAULT_MARK_MERGE_DISTANCE_M, } = {}) {
    if (track.measured.coords.length < 2)
        return [];
    const passes = anchors.flatMap((anchor) => passesNear(track.measured, anchor, snapDistanceM));
    return mergePasses(passes, mergeDistanceM).map(({ m, offsetM }) => ({
        trackId: track.id,
        m,
        offsetM,
        ele: elevationAt(track.profile, m) ?? positionAt(track.measured, m).ele,
        lngLat: positionAt(track.measured, m).lngLat,
    }));
}
/**
 * Marks of one item on every track. A line is marked at its midpoint; where
 * the midpoint isn't near a track (a line crossing it), at its endpoints.
 */
export function itemTrackMarks(item, tracks, options = {}) {
    const anchors = itemAnchors(item);
    if (!anchors.length)
        return [];
    return tracks.flatMap((track) => {
        const primary = trackMarks(anchors.slice(0, 1), track, options);
        if (primary.length || anchors.length === 1)
            return primary;
        return trackMarks(anchors.slice(1), track, options);
    });
}
/** Track stretches inside any of the item's view areas, sampled every 10 m. */
export function viewAreaCoverage(origin, areas, track, stepM = 10) {
    const measured = track.measured;
    const polygons = areas
        .filter((vertices) => vertices.length >= 3)
        .map((vertices) => viewAreaToLngLats(origin, vertices).map((p) => measured.projection.toXY(p)));
    if (!polygons.length || measured.length <= 0)
        return [];
    const ranges = [];
    let start = null;
    let last = 0;
    for (let m = 0; m <= measured.length; m += stepM) {
        const xy = positionAt(measured, m).xy;
        const inside = polygons.some((polygon) => pointInPolygon(xy, polygon));
        if (inside) {
            if (start === null)
                start = m;
            last = m;
        }
        else if (start !== null) {
            ranges.push({ trackId: track.id, fromM: start, toM: last });
            start = null;
        }
    }
    if (start !== null)
        ranges.push({ trackId: track.id, fromM: start, toM: last });
    return ranges;
}
/** Track stretch between the projections of a line's two ends. */
export function lineCoverage(line, track, snapDistanceM = DEFAULT_MARK_SNAP_DISTANCE_M) {
    if (line.length < 2)
        return null;
    const a = nearestOnTrack(track.measured, line[0]);
    const b = nearestOnTrack(track.measured, line[line.length - 1]);
    if (!a || !b)
        return null;
    // Both ends must be near the track: a far end would project onto some
    // unrelated part of a loop and claim the whole stretch in between
    if (Math.max(a.distanceM, b.distanceM) > snapDistanceM)
        return null;
    if (Math.abs(a.m - b.m) <= 5)
        return null;
    return {
        trackId: track.id,
        fromM: Math.min(a.m, b.m),
        toM: Math.max(a.m, b.m),
    };
}
/**
 * Re-project a follow-track line's endpoint onto a replaced track, picking
 * the pass nearest the old distance.
 */
export function reprojectDistance(track, point, previousM, snapDistanceM = 500) {
    const passes = passesNear(track, point, snapDistanceM);
    if (!passes.length) {
        return nearestOnTrack(track, point)?.m ?? Math.min(previousM, track.length);
    }
    return passes.reduce((best, p) => Math.abs(p.m - previousM) < Math.abs(best.m - previousM) ? p : best).m;
}
